<template>
  <div class="app-layout">
    <div class="lang-pill-container" v-if="!token">
      <div class="lang-pill">
        <button @click="changeLanguage('it')" class="lang-chip" :class="{ active: $i18n.locale === 'it' }">IT</button>
        <button @click="changeLanguage('en')" class="lang-chip" :class="{ active: $i18n.locale === 'en' }">EN</button>
        <button @click="changeLanguage('es')" class="lang-chip" :class="{ active: $i18n.locale === 'es' }">ES</button>
      </div>
    </div>
    <main class="app-content">
      <router-view/>
    </main>
    <ModalsContainer />

    <!-- Toast Globale per Nuova Prenotazione Odierna (Udibile e Visibile in qualsiasi vista) -->
    <transition name="toast-slide">
      <div v-if="toastPrenotazione" class="global-prenotazione-toast" @click="dismissReservationToast">
        <div class="toast-bell-circle">
          <i class="fas fa-bell"></i>
        </div>
        <div class="toast-content">
          <div class="toast-head">
            <span class="toast-badge-today">OGGI</span>
            <span class="toast-title">{{ toastPrenotazione.sala }}</span>
          </div>
          <div class="toast-detail">
            <strong>{{ toastPrenotazione.nome }}</strong> — {{ toastPrenotazione.coperti }} coperti alle <strong>{{ toastPrenotazione.ora }}</strong>
            <span v-if="toastPrenotazione.tavolo"> (Tavolo {{ toastPrenotazione.tavolo }})</span>
          </div>
        </div>
        <button type="button" class="toast-dismiss-btn" @click.stop="dismissReservationToast">&times;</button>
      </div>
    </transition>
  </div>
</template>

<script>
import moment from 'moment'
import { io } from "socket.io-client"
import { store } from './store/store.js'
import { ModalsContainer } from 'vue-final-modal'
import newOrderSound from '@/assets/newOrder.mp3'

let ipcRenderer = null;
try {
  if (window.require) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }
} catch (e) {
  console.log('Electron ipcRenderer not available');
}

export default {
  name: 'App',
  components: {
    ModalsContainer
  },
  data: function() {
    return {
      token: false,
      socket: io(process.env.VUE_APP_SOCKET_URL || "https://back.mesapilot.com/", { autoConnect: true }),
      prodotti: [],
      audioPlayer: null,
      asportoPollInterval: null,
      prenotazioniPollInterval: null,
      roomSettings: [],
      toastPrenotazione: null,
      toastPrenotazioneTimer: null,
      seenBookingKeys: new Set(),
      isInitialBookingScan: true,
      store
    }
  },
  methods: {
    changeLanguage(lang) {
      this.$i18n.locale = lang;
      localStorage.setItem('lang', lang);
    },
    fetchPrenotazioniSettings() {
      if (!localStorage.token) return;
      const token = (localStorage.token.startsWith('{') || localStorage.token.startsWith('"')) ? JSON.parse(localStorage.token) : localStorage.token;
      this.axios.get(this.store.path + 'prenotazioni-setting', {
        headers: { authorization: `Bearer ${token}` }
      }).then(res => {
        if (Array.isArray(res.data)) {
          this.roomSettings = res.data.filter(s => s.codicesala && s.codicesala.toLowerCase() !== 'asporto');
          console.log("Configurazione sale prenotazioni caricata:", this.roomSettings.length);
        }
      }).catch(err => {
        console.warn("Impossibile caricare impostazioni prenotazioni in App.vue:", err);
      });
    },
    isSameDayAndCurrentService(booking) {
      if (!booking) return false;
      const now = moment();
      let bookingMoment = null;

      if (booking.tm) {
        const tmNum = Number(booking.tm);
        bookingMoment = tmNum < 1e11 ? moment.unix(tmNum) : moment(tmNum);
      } else if (booking.data) {
        const timeStr = booking.ora || '12:00';
        bookingMoment = moment(`${booking.data} ${timeStr}`, 'YYYY-MM-DD HH:mm');
      }

      if (!bookingMoment || !bookingMoment.isValid()) {
        return false;
      }

      // 1. Controllo giorno stesso (oggi)
      if (!bookingMoment.isSame(now, 'day')) {
        return false;
      }

      // 2. Controllo configurazione per la sala
      const roomName = (booking.sala || booking.codicesala || '').toString().trim().toLowerCase();
      const roomSetting = this.roomSettings.find(s => 
        (s.codicesala && s.codicesala.toString().trim().toLowerCase() === roomName) ||
        (s.nomeSala && s.nomeSala.toString().trim().toLowerCase() === roomName)
      );

      // Se la sala ha esplicitamente disabilitato le prenotazioni, non notificare
      if (roomSetting && roomSetting.attiva === false) {
        return false;
      }

      // 3. Orario prenotazione e determinazione turno (pranzo / cena)
      const bookingHour = bookingMoment.hours();
      const bookingTimeStr = bookingMoment.format('HH:mm');

      let isLunchBooking = false;
      let isDinnerBooking = false;

      if (roomSetting && roomSetting.pranzoStart && roomSetting.pranzoEnd) {
        isLunchBooking = bookingTimeStr >= roomSetting.pranzoStart && bookingTimeStr <= roomSetting.pranzoEnd;
      } else {
        isLunchBooking = bookingHour >= 11 && bookingHour <= 15;
      }

      if (roomSetting && roomSetting.cenaStart && roomSetting.cenaEnd) {
        isDinnerBooking = bookingTimeStr >= roomSetting.cenaStart && bookingTimeStr <= roomSetting.cenaEnd;
      } else {
        isDinnerBooking = bookingHour >= 18 && bookingHour <= 23;
      }

      // 4. Determinazione del servizio attuale
      const currentTimeStr = now.format('HH:mm');
      const lunchCutoff = (roomSetting && roomSetting.pranzoEnd) ? roomSetting.pranzoEnd : "15:30";

      const isCurrentServiceLunch = currentTimeStr <= lunchCutoff;
      const isCurrentServiceDinner = currentTimeStr > lunchCutoff;

      if (isCurrentServiceLunch && isLunchBooking) {
        if (roomSetting && roomSetting.pranzoAttivo === false) return false;
        return true;
      }

      if (isCurrentServiceDinner && isDinnerBooking) {
        if (roomSetting && roomSetting.cenaAttivo === false) return false;
        return true;
      }

      // Se la prenotazione per cena arriva nel pomeriggio per la sera stessa
      if (isDinnerBooking && now.hours() >= 15) {
        if (roomSetting && roomSetting.cenaAttivo === false) return false;
        return true;
      }

      return false;
    },
    playReservationSound() {
      console.log("🔔 Riproduzione audio notifica prenotazione odierna!");
      try {
        if (!this.audioPlayer) {
          this.audioPlayer = new Audio(newOrderSound);
        }
        this.audioPlayer.currentTime = 0;
        const playPromise = this.audioPlayer.play();
        if (playPromise !== undefined) {
          playPromise.catch(err => {
            console.warn("Autoplay Audio element bloccato, uso Web Audio fallback:", err);
            this.playWebAudioChime();
          });
        }
      } catch (e) {
        console.error("Audio error, uso Web Audio fallback:", e);
        this.playWebAudioChime();
      }
      this.playWebAudioChime();
    },
    playWebAudioChime() {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        const playTone = (freq, startTime, duration) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, startTime);
          gain.gain.setValueAtTime(0.3, startTime);
          gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(startTime);
          osc.stop(startTime + duration);
        };
        const now = ctx.currentTime;
        playTone(659.25, now, 0.35);        // E5
        playTone(880.00, now + 0.15, 0.55); // A5
      } catch (err) {
        console.warn("Web Audio chime error:", err);
      }
    },
    handleIncomingReservation(booking) {
      if (!booking) return;
      const bookingKey = String(booking.id || booking._id || (booking.sala + '_' + (booking.tavolo || '') + '_' + booking.tm));
      if (this.seenBookingKeys.has(bookingKey)) {
        return;
      }
      this.seenBookingKeys.add(bookingKey);

      // Se le impostazioni delle sale non sono ancora caricate, caricale
      if (this.roomSettings.length === 0) {
        this.fetchPrenotazioniSettings();
      }

      // Verifica se la prenotazione è per OGGI e per il SERVIZIO ATTUALE
      if (this.isSameDayAndCurrentService(booking)) {
        console.log("🔔 Prenotazione per oggi ricevuta, emissione suono audio:", booking);
        this.playReservationSound();

        const tmMoment = booking.tm ? (Number(booking.tm) < 1e11 ? moment.unix(Number(booking.tm)) : moment(Number(booking.tm))) : moment();
        this.toastPrenotazione = {
          sala: booking.sala || booking.codicesala || 'Sala',
          tavolo: booking.tavolo ? (String(booking.tavolo).startsWith('t') ? String(booking.tavolo).slice(1) : booking.tavolo) : '',
          nome: `${booking.nome || ''} ${booking.cognome || ''}`.trim() || 'Cliente',
          coperti: booking.coperti || 2,
          ora: booking.ora || tmMoment.format('HH:mm')
        };

        if (this.toastPrenotazioneTimer) clearTimeout(this.toastPrenotazioneTimer);
        this.toastPrenotazioneTimer = setTimeout(() => {
          this.toastPrenotazione = null;
        }, 12000);
      }
    },
    async checkTodayBookings() {
      if (!localStorage.token) return;
      try {
        const token = (localStorage.token.startsWith('{') || localStorage.token.startsWith('"')) ? JSON.parse(localStorage.token) : localStorage.token;
        const res = await this.axios.get(this.store.path + 'prenotazioni-mgmt/giorno', {
          headers: { authorization: `Bearer ${token}` }
        });
        if (Array.isArray(res.data)) {
          res.data.forEach(booking => {
            const key = String(booking._id || (booking.codicesala + '_' + booking.tm));
            if (!this.seenBookingKeys.has(key)) {
              if (this.isInitialBookingScan) {
                this.seenBookingKeys.add(key);
              } else {
                this.handleIncomingReservation(booking);
              }
            }
          });
          this.isInitialBookingScan = false;
        }
      } catch (e) {
        // Ignora silenziosamente errori di rete
      }
    },
    dismissReservationToast() {
      this.toastPrenotazione = null;
      if (this.toastPrenotazioneTimer) clearTimeout(this.toastPrenotazioneTimer);
    },
    playNewOrderSound() {
      try {
        if (!this.audioPlayer) {
          this.audioPlayer = new Audio(newOrderSound);
        }
        this.audioPlayer.currentTime = 0;
        let playPromise = this.audioPlayer.play();
        if (playPromise !== undefined) {
          playPromise.catch(err => {
            console.warn("Autoplay audio blocked or error:", err);
          });
        }
      } catch (e) {
        console.error("Errore riproduzione audio ordine asporto:", e);
      }
    },
    async checkAndPrintAsportoOrders() {
      if (!localStorage.token) return;
      try {
        const tokenRaw = localStorage.token;
        const tokenStr = (tokenRaw.startsWith('{') || tokenRaw.startsWith('"')) ? JSON.parse(tokenRaw) : tokenRaw;
        const today = new Date().toISOString().split('T')[0];

        const response = await this.axios.post(this.store.path + 'getOrdiniAsporto', {
          data: today
        }, {
          headers: { authorization: `Bearer ${tokenStr}` }
        });

        if (response.data && Array.isArray(response.data.messaggio) && response.data.messaggio.length > 0) {
          const newOrders = response.data.messaggio.filter(o => 
            !o.isPrinted && (
              (o.pagato && o.dataRitiro === today) || 
              o.isStaff
            )
          );

          if (newOrders.length > 0) {
            console.log("🔔 Ordini asporto non stampati trovati in App.vue:", newOrders.length);
            
            // 1. Suono alert globale
            this.playNewOrderSound();

            // 2. Stampa automatica receipt (se in ambiente Electron)
            if (ipcRenderer) {
              try {
                const printerRes = await this.axios.post(this.store.path + 'displaySpecificPrinter', {
                  nome: 'asporto'
                }, {
                  headers: { authorization: `Bearer ${tokenStr}` }
                });

                const printers = printerRes.data.messaggio;
                if (printers && printers.length) {
                  const printer = printers[0];

                  for (const ordine of newOrders) {
                    console.log('📠 Stampa automatica ordine asporto globale:', ordine._id);
                    const sanitizedOrder = JSON.parse(JSON.stringify({
                      ord: ordine.ordinazione.map(item => ({
                        ...item,
                        piatto: item.piatto,
                        aggiunta: item.aggiunta || [],
                        senza: item.senza || [],
                        prezzo: item.prezzo / 100,
                        countGroup: item.quantita
                      })),
                      nome: ordine.nome,
                      email: ordine.email,
                      telefono: ordine.telefono,
                      dataRitiro: ordine.dataRitiro,
                      oraRitiro: ordine.oraRitiro,
                      totale: ordine.totale / 100
                    }));

                    ipcRenderer.send('print', sanitizedOrder, { 
                      ip: printer.ip, 
                      porta: printer.porta 
                    }, 'asporto');

                    await this.axios.post(this.store.path + 'segnaAsportoStampato', {
                      id: ordine._id
                    }, {
                      headers: { authorization: `Bearer ${tokenStr}` }
                    });

                    ordine.isPrinted = true;
                  }
                }
              } catch (err) {
                console.error("Errore stampa automatica asporto in App.vue:", err);
              }
            }
          }
        }
      } catch (err) {
        console.warn("Errore controllo ordini asporto in App.vue:", err);
      }
    },
    displayProduct() {
      this.axios.post(this.store.path+'displayProduct',{headers:{
      authorization: `Bearer  ${JSON.parse(localStorage.token)}`}}).then((response) => {
        if(response.data.messaggio){
          console.log('Prodotti caricati con successo')
        }
        response.data.messaggio.forEach(element => {
          this.prodotti.push(element);
        });
      })
    },
    printPaper(rv) {
      if (!ipcRenderer) return;
      this.axios.post(this.store.path + 'displayPrinter', {
        headers: {
          authorization: `Bearer  ${JSON.parse(localStorage.token)}`
        }
      })
      .then((response) => {
        if (response.data.messaggio && rv.length) {
          const printers = response.data.messaggio;
          printers.forEach(printer => {
            let shouldPrint = false;
            rv.forEach(tableOrder => {
              if (tableOrder.ord) {
                const hasMatchingItem = tableOrder.ord.some(item => {
                  if (!item.portata) return false;
                  const itemPortata = item.portata.trim().toLowerCase();
                  if (Array.isArray(printer.postazioni)) {
                    return printer.postazioni.some(p => p.trim().toLowerCase() === itemPortata);
                  } else if (typeof printer.postazioni === 'string') {
                    return printer.postazioni.toLowerCase().split(',').map(s => s.trim()).includes(itemPortata);
                  }
                  return false;
                });
                if (hasMatchingItem) shouldPrint = true;
              }
            });

            if (shouldPrint) {
              ipcRenderer.send('print', rv, {
                ip: printer.ip,
                porta: printer.porta
              });
            }
          });

          rv.forEach(tableOrder => {
            if (tableOrder.ord && tableOrder.ord.length > 0) {
              const piattoIds = tableOrder.ord.map(p => p._id);
              this.axios.post(this.store.path + 'markAsPrinted', {
                ordineId: tableOrder.idO,
                piattoIds: piattoIds
              }, {
                headers: { authorization: `Bearer ${JSON.parse(localStorage.token)}` }
              }).catch(err => console.error(err));
            }
          });
        }
      })
    },
    getDataOrdine (tav,sal) {
      this.moment = moment;
      var end = moment(this.date).endOf('day').unix();
      var start = moment(this.date).startOf('day').unix();
      var data = moment(this.date).format('DD');
      var rv= [];
      var totale = 0;
      
      this.axios.post(this.store.path+'displayOrdine',{codtavolo:tav,sala:sal,end:end,start:start,singolo:true,headers:{
        authorization: `Bearer  ${JSON.parse(localStorage.token)}`}}).then((response) => {
          if(response.data){
            response.data.messaggio.forEach(element => {
                var lastPortata = '';
                const piattiDaStampare = element.ordine.filter(ord => !ord.isPrinted);
                
                if (piattiDaStampare.length === 0) return;

                piattiDaStampare.forEach(ord => {
                    lastPortata = ord.portata;
                    let itemCostoAggiunta = 0;
                    if (ord.aggiunta && ord.aggiunta.length) {
                        ord.aggiunta.forEach(aggiunta => {
                            if (typeof aggiunta === 'object' && aggiunta !== null) {
                                if (aggiunta.prezzo !== undefined && aggiunta.prezzo !== null) {
                                    itemCostoAggiunta += Number(aggiunta.prezzo) || 0;
                                } else {
                                    const desc = aggiunta.descrizione || aggiunta.nome || aggiunta.descrizio;
                                    const prod = this.prodotti.find(o => o.descrizio === desc || o.nome === desc);
                                    if (prod && prod.prezzoAggiunta) {
                                        itemCostoAggiunta += Number(prod.prezzoAggiunta) || 0;
                                    }
                                }
                            } else if (typeof aggiunta === 'string') {
                                const prod = this.prodotti.find(o => o.descrizio === aggiunta || o.nome === aggiunta);
                                if (prod && prod.prezzoAggiunta) {
                                    itemCostoAggiunta += Number(prod.prezzoAggiunta) || 0;
                                }
                            }
                        });
                    }
                    const qty = ord.quantita || 1;
                    ord.costoAggiunta = itemCostoAggiunta * qty;
                    totale = totale + (ord.costoAggiunta ? ord.costoAggiunta : 0) + ord.prezzo;
                })

                rv.push({
                  idT: element.idTavolo,
                  idO: element.idOrdine,
                  t: element.tavolo, 
                  ord: piattiDaStampare,
                  tm: data,
                  portata: lastPortata, 
                  sala: sal
                })
            });

            if (rv.length > 0) {
                this.printPaper([...rv])
            }
          }
        })
    }
  },
  beforeMount() {
    this.socket.on('ricevutoOrdine', (messaggio) => {
      let tavoloId = messaggio.tavolo.toString().includes("t") ? messaggio.tavolo.split("t")[1].toString() : messaggio.tavolo.toString();
      let salaNome = messaggio.sala && messaggio.sala.nome ? messaggio.sala.nome : messaggio.sala;
      this.getDataOrdine(tavoloId, salaNome);
      let element = document.getElementById(tavoloId);
      if (element) {
        element.classList.add('ordinato');
      }
    });

    this.socket.on('ricevutoOrdineAsporto', (data) => {
      console.log("🔔 Socket ricevutoOrdineAsporto in App.vue:", data);
      this.checkAndPrintAsportoOrders();
    });

    this.socket.on('ricevutaPrenotazione', (data) => {
      console.log("🔔 Socket ricevutaPrenotazione in App.vue:", data);
      this.handleIncomingReservation(data);
    });

    this.socket.on('aggiornaPrenotazioniData', () => {
      console.log("🔔 Socket aggiornaPrenotazioniData in App.vue");
      this.checkTodayBookings();
    });
  },
  mounted() {
    this.fetchPrenotazioniSettings();
    this.checkTodayBookings();
    this.prenotazioniPollInterval = setInterval(() => {
      this.checkTodayBookings();
    }, 15000);

    this.checkAndPrintAsportoOrders();
    this.asportoPollInterval = setInterval(() => {
      this.checkAndPrintAsportoOrders();
    }, 10000);

    const unlockAudio = () => {
      if (this.audioPlayer) {
        this.audioPlayer.play().then(() => {
          this.audioPlayer.pause();
          this.audioPlayer.currentTime = 0;
        }).catch(() => {});
      }
      window.removeEventListener('click', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
    window.addEventListener('click', unlockAudio);
    window.addEventListener('keydown', unlockAudio);
  },
  beforeUnmount() {
    if (this.asportoPollInterval) {
      clearInterval(this.asportoPollInterval);
    }
    if (this.prenotazioniPollInterval) {
      clearInterval(this.prenotazioniPollInterval);
    }
    if (this.toastPrenotazioneTimer) {
      clearTimeout(this.toastPrenotazioneTimer);
    }
  },
  created() {
    const savedLang = localStorage.getItem('lang');
    if (savedLang) {
      this.$i18n.locale = savedLang;
    }
  },
  updated() {
    this.token = localStorage.token;
  }
}
</script>

<style>
#app {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--sci-text-main, #0f172a);
  background-color: var(--sci-bg, #f8fafc);
  padding: 0;
  width: 100vw;
  min-height: 100vh;
  margin: 0;
  overflow-x: hidden;
}

body {
  min-height: 100vh;
  padding: 0;
  margin: 0;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
}

.app-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-content {
  flex: 1;
}

.lang-pill-container {
  position: absolute;
  top: 14px;
  right: 18px;
  z-index: 1050;
}

.lang-pill {
  display: inline-flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  padding: 3px 4px;
  border-radius: 9999px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.lang-chip {
  border: none;
  background: transparent;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 9999px;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
}

.lang-chip:hover {
  color: #0f172a;
}

.lang-chip.active {
  background: #4f46e5;
  color: #ffffff;
  box-shadow: 0 1px 3px rgba(79, 70, 229, 0.3);
}

#nav {
  padding-top: 2vh;
  height: 8vh;
}

#nav a {
  font-weight: bold;
  color: #2c3e50;
}

#nav a.router-link-exact-active {
  color: #42b983;
}

/* Toast Globale Nuova Prenotazione Odierna */
.global-prenotazione-toast {
  position: fixed;
  top: 18px;
  right: 24px;
  z-index: 999999;
  background: #0f172a;
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-left: 6px solid #d97706;
  border-radius: 16px;
  padding: 16px 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: 440px;
  cursor: pointer;
  animation: pulse-ring 2s infinite;
  transition: all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.global-prenotazione-toast:hover {
  transform: translateY(-2px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.45);
}

.toast-bell-circle {
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 12px;
  background: #fef3c7;
  color: #d97706;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  animation: bell-bounce 1s infinite alternate;
}

@keyframes bell-bounce {
  0% { transform: rotate(-10deg); }
  100% { transform: rotate(10deg); }
}

.toast-content {
  flex: 1;
}

.toast-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.toast-badge-today {
  background: #d97706;
  color: #ffffff;
  font-size: 10px;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
  letter-spacing: 0.5px;
}

.toast-title {
  font-size: 14px;
  font-weight: 800;
  color: #ffffff;
  text-transform: capitalize;
}

.toast-detail {
  font-size: 13px;
  color: #cbd5e1;
  line-height: 1.4;
}

.toast-dismiss-btn {
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 20px;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  transition: color 0.15s ease;
}

.toast-dismiss-btn:hover {
  color: #ffffff;
}

.toast-slide-enter-active, .toast-slide-leave-active {
  transition: all 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.toast-slide-enter-from, .toast-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}
</style>
